import {useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  fetchActiveSessionCounts,
  fetchSessionChallengeList,
  fetchSessionCoverage,
  selectActiveSessionStatus,
  selectSessionChallengeList,
  selectSessionCoverage,
} from '../slices/marketSlice';
import {selectAuthIsForecaster} from '../slices/authSlice';
import {deriveMarketsOverview} from '../utils/marketsOverview';

/**
 * Cross-market submission overview for the open session (forecasters only).
 *
 * Always fetches the session-wide challenge list when a session is open.
 * Pass fetchBase (+ optional refreshMs) on consumers mounted outside the
 * dashboard (Header) so session counts and coverage exist there too; the
 * StatusHero already polls both on its own cadence.
 *
 * @returns the deriveMarketsOverview() view-model, or null (observer /
 * no open session / no challenges yet).
 */
export default function useMarketsOverview({fetchBase = false, refreshMs = null} = {}) {
  const dispatch = useDispatch();
  const isForecaster = useSelector(selectAuthIsForecaster);
  const sessionStatus = useSelector(selectActiveSessionStatus);
  const challengeList = useSelector(selectSessionChallengeList);
  const sessionCoverage = useSelector(selectSessionCoverage);
  const resources = useSelector((state) => state.user.resources);

  useEffect(() => {
    if (!fetchBase || !isForecaster) return undefined;
    const load = () => {
      dispatch(fetchActiveSessionCounts());
      dispatch(fetchSessionCoverage());
    };
    load();
    if (!refreshMs) return undefined;
    const t = setInterval(load, refreshMs);
    return () => clearInterval(t);
  }, [dispatch, fetchBase, isForecaster, refreshMs]);

  useEffect(() => {
    if (!isForecaster) return;
    const sessionId = sessionStatus.sessionId;
    if (sessionId && challengeList.sessionId !== sessionId) {
      dispatch(fetchSessionChallengeList({sessionId}));
    }
  }, [dispatch, isForecaster, sessionStatus.sessionId, challengeList.sessionId]);

  return useMemo(() => {
    if (!sessionStatus.isOpen || !sessionStatus.sessionId) return null;
    if (challengeList.sessionId !== sessionStatus.sessionId) return null;
    const gateMs = sessionStatus.gateClosureAt ? new Date(sessionStatus.gateClosureAt).getTime() : null;
    const closed = gateMs != null && gateMs <= Date.now();
    return deriveMarketsOverview({
      resources: resources || [],
      challenges: challengeList.challenges,
      coverage: sessionCoverage?.byChallenge || {},
      isForecaster,
      closed,
    });
  }, [sessionStatus, challengeList, sessionCoverage, resources, isForecaster]);
}
